import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseComponent, SpinnerComponent } from '@lib/components';
import { ApiResponseError, ICreateCheckResponse, IDVReportDetails, ISdkToken } from '@lib/interfaces';
import { CustomersService } from '@lib/services/customer/customer.service';
import { interval, Subject } from 'rxjs';
import { switchMap, takeUntil, finalize } from 'rxjs/operators';
import { AngularMaterialModule } from 'src/app/material.module';

declare let identitySdk: any;

@Component({
    selector: 'app-spotti-facial-verification',
    templateUrl: './spotti-facial-verification.component.html',
    styleUrls: ['./spotti-facial-verification.component.scss'],
    standalone: true,
    imports: [AngularMaterialModule, SpinnerComponent],
})
export class SpottiFacialVerificationComponent extends BaseComponent implements OnInit {
    userId: string;
    isLoading = false;
    @Output() donePressed = new EventEmitter<'clear' | 'consider'>();

    constructor(public dialogRef: MatDialogRef<SpottiFacialVerificationComponent>, private readonly customersService: CustomersService, @Inject(MAT_DIALOG_DATA) public data: { receiverId: string }, private readonly snackbar: MatSnackBar) {
        super();
    }

    ngOnInit() {
        this.userId = localStorage.getItem('userId') || '';
        this.customersService.generateSDKToken(this.data.receiverId).subscribe((sdkToken: ISdkToken) => {
            this.initiateSDK(sdkToken.token);
        });
    }

    public initiateSDK(token: string) {
        const sdkConfig = {
            sdkToken: token,
            environment: 'dev',
            containerId: 'verification-container',
            language: 'en',
            country: 'IRQ',
            flowType: 'facial_recognition',
            nfcSupported: false,
            onStepSuccess: (event: Event) => {
                console.log('onStepSuccess', event);
            },
            onStepFailure: (event: Event) => {
                console.log('onStepFailure', event);
            },
            onFlowSuccess: (event: Event) => {
                console.log('onFlowSuccess', event);
            },
            onFlowFailure: (event: Event) => {
                console.log('onFlowFailure', event);
            },
            onDonePressed: (event: Event) => {
                console.log('onDonePressed', event);
                this.createCheck();
            },
            customStyles: {
                fontName: 'Readex Pro',
                textColor: '#1A2967',
                containerBackgroundColor: '#FFF',
                buttonsColor: '#1A2967',
                buttonsTextColor: '#FFF',
                labelColor: '#162E6D',
                inputTextColor: '#1A2967',
                loaderColor: '#1A2967',
                loaderSize: '35px',
                frameWidth: '1000px',
                frameHeight: '800px',
                iconsColor: '#304DBB',
            },
        };

        identitySdk.initIdentitySDK(sdkConfig);
    }

    getUserIDVReportStatus(userId: string, reportId: string) {
       
        const unsubscribe$ = new Subject<void>();
        const maxRetries = 10;
        let attemptCount = 0;


        this.addSubscription(
            interval(3000)
                .pipe(
                    switchMap(() =>  this.customersService.fetchCustomerIdvReportVerificationStatus(userId, reportId)),
                    takeUntil(unsubscribe$),
                    finalize(() => {
                        if (attemptCount >= maxRetries) {
                            this.createApiErrorSnackBar(this.snackbar, 'IDV Report Generate failed. Please try again');
                            this.dialogRef.close();
                        }
                    }),
                )
                .subscribe({
                    next: (report: IDVReportDetails ) => {
                        attemptCount++;
                        
                        if (report.result) {
                            this.isLoading = false;
                            this.donePressed.emit(report.result);
                            unsubscribe$.next();
                            unsubscribe$.complete();
                        }

                        if (attemptCount > maxRetries) {
                            unsubscribe$.next();
                            unsubscribe$.complete();
                        }
                    },
                    error: (error: ApiResponseError) => {
                        this.isLoading = false;
                        this.createApiErrorSnackBar(this.snackbar, error);
                        unsubscribe$.next();
                        unsubscribe$.complete();
                    },
                }),
        );
    }

    createCheck() {
        this.isLoading = true;
        const payload = {
            user_id: this.data.receiverId,
            agent_id: this.userId,
            report_names: ['facial_similarity_video'],
            privacy_notices_read_consent_given: true,
            flow_type: 'TOPUP',
        };

        this.customersService.createChecks(payload).subscribe({
            next: (createCheckResponse: ICreateCheckResponse) => {
                this.getChecks(createCheckResponse.id);
            },
            error: (error: ApiResponseError) => {
                this.isLoading = false;
                this.createApiErrorSnackBar(this.snackbar, error);
            },
        });
    }

    getChecks(check_id: string) {
        const payload = {
            user_id: this.data.receiverId,
            agent_id: this.userId,
            check_id,
            is_agent: true,
        };

        const unsubscribe$ = new Subject<void>();
        const maxRetries = 30;
        let attemptCount = 0;

        this.addSubscription(
            interval(4000)
                .pipe(
                    switchMap(() => this.customersService.getChecks(payload)),
                    takeUntil(unsubscribe$),
                    finalize(() => {
                        if (attemptCount >= maxRetries) {
                            this.createApiErrorSnackBar(this.snackbar, 'IDV Checks have failed. Please try again');
                            this.dialogRef.close();
                        }
                    }),
                )
                .subscribe({
                    next: ({ data: checks }) => {
                        attemptCount++;

                        if (checks[0].report_ids.length > 0 && checks[0].status === 'completed') {
                            this.getUserIDVReportStatus(this.data.receiverId, checks[0].report_ids[0]);
                            unsubscribe$.next();
                            unsubscribe$.complete();
                        }

                        if (attemptCount > maxRetries) {
                            unsubscribe$.next();
                            unsubscribe$.complete();
                        }
                    },
                    error: (error: ApiResponseError) => {
                        this.isLoading = false;
                        this.createApiErrorSnackBar(this.snackbar, error);
                        unsubscribe$.next();
                        unsubscribe$.complete();
                    },
                }),
        );
    }
}
